import { useState } from 'react';
import { Link, Typography } from '@mui/material';

import { Button } from '@/components/ui/button';
import { MAX_FILE_SIZE_MB, SUPPORT_EMAIL } from '@/utils/constants';
import AssetGuidelinesModal from '../AssetGuidelinesModal';

interface FileErrorProps {
  name: string;
  storeArea?: string;
  reason?: string;
}

export function CampaignFetchError() {
  return (
    <Typography variant="body2" color="error">
      Something wrong happened, please refresh the page. If the problem
      persists, contact{' '}
      <Link href={`mailto:${SUPPORT_EMAIL}`} color="error" underline="none">
        {SUPPORT_EMAIL}
      </Link>
    </Typography>
  );
}

export function AssetTypeError({ name }: FileErrorProps) {
  return (
    <Typography variant="body2" color="error">
      {`The file "${name}" is not a supported file type. Please only upload one of the supported file types ( .jpg, .png, .mp4)`}
    </Typography>
  );
}

export function MediaReadError({ name, reason }: FileErrorProps) {
  return (
    <Typography variant="body2" color="error">
      {`The file "${name}" could not be read.`}
      {reason && ` ${reason}`}
    </Typography>
  );
}

export function VideoDurationError({ name }: FileErrorProps) {
  const [open, setOpen] = useState(false);

  return (
    <Typography variant="body2" color="error">
      The file “{name}” has a duration longer than we currently support. Please
      review{' '}
      <Button onClick={() => setOpen(true)} variant="link" size="inline">
        our asset guidelines for videos
      </Button>
      <AssetGuidelinesModal open={open} onClose={() => setOpen(false)} /> and
      then try your upload again.
    </Typography>
  );
}

export function VideoSizeError({ name }: FileErrorProps) {
  return (
    <Typography variant="body2" color="error">
      The file “{name}” is too large. Please reduce its file size to{' '}
      {MAX_FILE_SIZE_MB}MB or less.
    </Typography>
  );
}

export function AssetAspectError({ name }: FileErrorProps) {
  const [open, setOpen] = useState(false);

  return (
    <Typography variant="body2" color="error">
      The file “{name}” is not a supported aspect ratio. Please review our{' '}
      <Button onClick={() => setOpen(true)} variant="link" size="inline">
        asset guidelines
      </Button>
      <AssetGuidelinesModal open={open} onClose={() => setOpen(false)} /> and
      then try your upload again.
    </Typography>
  );
}

export function ImageAspectError({ name }: FileErrorProps) {
  const [open, setOpen] = useState(false);

  return (
    <Typography variant="body2" color="error">
      The file “{name}” is not a supported aspect ratio. Please review our{' '}
      <Button onClick={() => setOpen(true)} variant="link" size="inline">
        asset guidelines for images
      </Button>
      <AssetGuidelinesModal open={open} onClose={() => setOpen(false)} /> and
      then try your upload again.
    </Typography>
  );
}

export function VideoAspectError({ name }: FileErrorProps) {
  const [open, setOpen] = useState(false);

  return (
    <Typography variant="body2" color="error">
      The file “{name}” is not a supported aspect ratio. Please review our{' '}
      <Button onClick={() => setOpen(true)} variant="link" size="inline">
        asset guidelines for videos
      </Button>
      <AssetGuidelinesModal open={open} onClose={() => setOpen(false)} /> and
      then try your upload again.
    </Typography>
  );
}

export function AssetAspectWithStoreError({ name }: FileErrorProps) {
  const [open, setOpen] = useState(false);

  return (
    <Typography variant="body2" color="error">
      The file “{name}” is not the correct aspect ratio for the selected store
      areas. Please review our{' '}
      <Button onClick={() => setOpen(true)} variant="link" size="inline">
        asset guidelines
      </Button>
      <AssetGuidelinesModal open={open} onClose={() => setOpen(false)} /> and
      then try your upload again.
    </Typography>
  );
}

export function AssetResolutionError({ name }: FileErrorProps) {
  const [open, setOpen] = useState(false);

  return (
    <Typography variant="body2" color="error">
      The file “{name}” does not meet the minimum resolution requirement. Please
      review our{' '}
      <Button onClick={() => setOpen(true)} variant="link" size="inline">
        asset guidelines
      </Button>
      <AssetGuidelinesModal open={open} onClose={() => setOpen(false)} /> and
      then try your upload again.
    </Typography>
  );
}

export function VideoResolutionError({ name }: FileErrorProps) {
  const [open, setOpen] = useState(false);

  return (
    <Typography variant="body2" color="error">
      The file “{name}” does not meet the minimum resolution requirement. Please
      review our{' '}
      <Button onClick={() => setOpen(true)} variant="link" size="inline">
        asset guidelines for videos
      </Button>
      <AssetGuidelinesModal open={open} onClose={() => setOpen(false)} /> and
      then try your upload again.
    </Typography>
  );
}

export function ImageResolutionError({ name }: FileErrorProps) {
  const [open, setOpen] = useState(false);

  return (
    <Typography variant="body2" color="error">
      The file “{name}” does not meet the minimum resolution requirement. Please
      review our{' '}
      <Button onClick={() => setOpen(true)} variant="link" size="inline">
        asset guidelines for images
      </Button>
      <AssetGuidelinesModal open={open} onClose={() => setOpen(false)} /> and
      then try your upload again.
    </Typography>
  );
}
